import { useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { PageHeader, formatQty } from "@/components/Shared";
import { toast } from "sonner";
import { Coffee, Sunrise, Sunset, Plus, Trash2, AlertTriangle, Pencil, Check, X } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}

function daysAgoISO(days) {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
}

export default function CoffeeTracking() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin" || user?.role === "manager";
  const [branches, setBranches] = useState([]);
  const [branchId, setBranchId] = useState("");
  const [today, setToday] = useState(null);
  const [yesterdayWarn, setYesterdayWarn] = useState(null);
  const [history, setHistory] = useState([]);
  const [form, setForm] = useState({ morning: "", evening: "", refill: "", note: "", counters: [], personal: "" });
  const [range, setRange] = useState({ from: daysAgoISO(30), to: todayISO() });
  const [saving, setSaving] = useState(false);
  const [overrideMorning, setOverrideMorning] = useState(false);
  const [overrideEvening, setOverrideEvening] = useState(false);
  const [overrideRefill, setOverrideRefill] = useState(false);
  const [overrideCounters, setOverrideCounters] = useState(false);
  const [overridePersonal, setOverridePersonal] = useState(false);
  const [editRow, setEditRow] = useState(null); // {id, morning, evening, refill, counters}
  const [dateKey, setDateKey] = useState(todayISO());

  const activeBranchId = branchId || user?.branch_id || "";
  const activeBranch = branches.find((b) => b.id === activeBranchId);
  const grindersCount = activeBranch?.coffee_grinders_count ?? 2;

  const morningSaved = today?.morning_grams != null;
  const eveningSaved = today?.evening_grams != null;
  const countersSaved = Array.isArray(today?.counter_readings) && today.counter_readings.length > 0;
  const personalSaved = today?.personal_coffees != null;
  const morningLocked = morningSaved && !overrideMorning;
  const eveningLocked = eveningSaved && !overrideEvening;
  const countersLocked = countersSaved && !overrideCounters;
  const personalLocked = personalSaved && !overridePersonal;
  // Refill: personál nesmí měnit po večerním uzavření; admin/manager smí přes override
  const refillLocked = eveningSaved && !isAdmin && !overrideRefill;

  // Refresh na změnu dne (např. mobil přes noc otevřený)
  useEffect(() => {
    const tick = () => {
      const now = todayISO();
      if (now !== dateKey) setDateKey(now);
    };
    const t = setInterval(tick, 60_000);
    const onFocus = () => tick();
    window.addEventListener("focus", onFocus);
    return () => {
      clearInterval(t);
      window.removeEventListener("focus", onFocus);
    };
  }, [dateKey]);

  const load = async () => {
    const [b] = await Promise.all([api.get("/branches")]);
    setBranches(b.data);
    if (!branchId && user?.branch_id) setBranchId(user.branch_id);
    else if (!branchId && isAdmin && b.data.length > 0) setBranchId(b.data[0].id);
  };

  const loadToday = async () => {
    if (!activeBranchId) return;
    try {
      const [r, w] = await Promise.all([
        api.get("/coffee/readings/today", { params: { branch_id: activeBranchId } }),
        api.get("/coffee/readings/yesterday-warning", { params: { branch_id: activeBranchId } }),
      ]);
      setToday(r.data);
      const savedCounters = Array.isArray(r.data?.counter_readings) ? r.data.counter_readings : [];
      setForm({
        morning: r.data?.morning_grams ?? "",
        evening: r.data?.evening_grams ?? "",
        refill: r.data?.refill_grams ?? "",
        note: r.data?.note ?? "",
        counters: savedCounters,
        personal: r.data?.personal_coffees ?? "",
      });
      setOverrideMorning(false);
      setOverrideEvening(false);
      setOverrideRefill(false);
      setOverrideCounters(false);
      setOverridePersonal(false);
      setYesterdayWarn(w.data?.warn ? w.data : null);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Chyba");
    }
  };

  const loadHistory = async () => {
    if (!isAdmin) return;
    try {
      const r = await api.get("/coffee/readings", {
        params: { from_date: range.from, to_date: range.to },
      });
      setHistory(r.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Chyba");
    }
  };

  useEffect(() => { load(); /* eslint-disable-line */ }, []);
  useEffect(() => { loadToday(); /* eslint-disable-line */ }, [activeBranchId, dateKey]);
  useEffect(() => { loadHistory(); /* eslint-disable-line */ }, [range, activeBranchId, dateKey]);

  const submitPart = async (part) => {
    if (!activeBranchId) return toast.error("Vyberte pobočku");
    const payload = { branch_id: activeBranchId, date: todayISO() };
    if (part === "morning") payload.morning_grams = parseFloat(form.morning);
    if (part === "evening") payload.evening_grams = parseFloat(form.evening);
    if (part === "refill") payload.refill_grams = parseFloat(form.refill) || 0;
    if (part === "note") payload.note = form.note;
    if (part === "counters") {
      const arr = (form.counters || []).slice(0, grindersCount);
      while (arr.length < grindersCount) arr.push(0);
      const cleaned = arr.map((c) => (c === "" || c === null || c === undefined ? NaN : parseFloat(c)));
      if (cleaned.some((c) => !Number.isFinite(c))) {
        return toast.error("Zadejte hodnoty počítadel pro všechny mlýnky");
      }
      payload.counter_readings = cleaned;
    }
    if (part === "personal") {
      const v = parseFloat(form.personal);
      if (!Number.isFinite(v) || v < 0) {
        return toast.error("Zadejte platný počet personálních káv");
      }
      payload.personal_coffees = v;
    }
    if ((part === "morning" || part === "evening") && !Number.isFinite(payload[part === "morning" ? "morning_grams" : "evening_grams"])) {
      return toast.error("Zadejte platnou hmotnost");
    }
    setSaving(true);
    try {
      await api.post("/coffee/readings", payload);
      toast.success("Uloženo");
      await Promise.all([loadToday(), loadHistory()]);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Chyba");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Smazat záznam?")) return;
    try {
      await api.delete(`/coffee/readings/${id}`);
      toast.success("Smazáno");
      await Promise.all([loadToday(), loadHistory()]);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Chyba");
    }
  };

  const startEditRow = (r) => {
    setEditRow({
      id: r.id,
      branch_id: r.branch_id,
      date: r.date,
      morning: r.morning_grams ?? "",
      evening: r.evening_grams ?? "",
      refill: r.refill_grams ?? "",
    });
  };

  const cancelEditRow = () => setEditRow(null);

  const saveEditRow = async () => {
    if (!editRow) return;
    const payload = {
      branch_id: editRow.branch_id,
      date: editRow.date,
    };
    if (editRow.morning !== "" && editRow.morning !== null) payload.morning_grams = parseFloat(editRow.morning);
    if (editRow.evening !== "" && editRow.evening !== null) payload.evening_grams = parseFloat(editRow.evening);
    if (editRow.refill !== "" && editRow.refill !== null) payload.refill_grams = parseFloat(editRow.refill) || 0;
    try {
      await api.post("/coffee/readings", payload);
      toast.success("Uloženo");
      setEditRow(null);
      await Promise.all([loadToday(), loadHistory()]);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Chyba");
    }
  };

  const totalPortions = useMemo(
    () => history.reduce((s, r) => s + (r.expected_portions || 0), 0),
    [history]
  );

  return (
    <div data-testid="coffee-page">
      <PageHeader
        eyebrow="Káva"
        title="Vážení kávy v mlýncích"
        description="Zaznamenej ráno hmotnost kávy v mlýncích, večer stav po směně. Systém spočítá, kolik káv mělo být prodáno."
      />

      {isAdmin && (
        <div className="mb-5">
          <div className="text-xs uppercase tracking-wider text-[#57534E] mb-1.5">Pobočka</div>
          <select
            data-testid="coffee-branch-select"
            className="input-cafe w-full sm:w-auto"
            value={activeBranchId}
            onChange={(e) => setBranchId(e.target.value)}
          >
            {branches.map((b) => (
              <option key={b.id} value={b.id}>{b.name}</option>
            ))}
          </select>
        </div>
      )}

      {yesterdayWarn?.warn && (
        <div
          data-testid="coffee-yesterday-warn"
          className="mb-5 p-4 rounded-lg border flex items-start gap-3"
          style={{ background: "#FFF7E6", borderColor: "#E0B84D66", color: "#7A5A17" }}
        >
          <AlertTriangle className="w-5 h-5 mt-0.5 shrink-0" />
          <div className="text-sm">
            <div className="font-semibold mb-0.5">Včera ({yesterdayWarn.date}) nebylo vyplněno večerní vážení.</div>
            <div className="text-xs opacity-80">
              Spotřebu za včerejšek nelze spočítat. {isAdmin ? "Můžeš hodnotu dopsat v historii níže." : "Kontaktuj manažera pro doplnění."}
            </div>
          </div>
        </div>
      )}

      {activeBranch && (
        <div className="card-cafe p-5 sm:p-6 mb-6">
          <div className="flex items-center gap-2 mb-3">
            <Coffee className="w-5 h-5 text-[#3A2D27]" />
            <h3 className="text-lg font-semibold" style={{ fontFamily: "Outfit" }}>
              Dnes ({todayISO()}) — {activeBranch.name}
            </h3>
            <span className="text-xs text-[#57534E] ml-2">
              {activeBranch.coffee_grinders_count ?? 2} mlýnky · {activeBranch.coffee_portion_grams ?? 7} g/porce
            </span>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {/* RÁNO */}
            <label className="block">
              <div className="text-xs uppercase tracking-wider text-[#57534E] mb-1.5 inline-flex items-center gap-1">
                <Sunrise className="w-3.5 h-3.5" /> Ráno (g v mlýncích celkem + káva balíčky)
                {morningSaved && (
                  <span className="ml-1 text-[10px] px-1.5 py-0.5 rounded bg-[#E5E1D8] text-[#57534E]">Uloženo</span>
                )}
              </div>
              <div className="flex gap-2">
                <input
                  data-testid="coffee-morning-input"
                  type="number"
                  inputMode="decimal"
                  step="1"
                  min="0"
                  disabled={morningLocked}
                  className={`input-cafe w-full text-xl ${morningLocked ? "opacity-60 cursor-not-allowed" : ""}`}
                  placeholder="např. 800"
                  value={form.morning}
                  onChange={(e) => setForm({ ...form, morning: e.target.value })}
                />
                {isAdmin && morningSaved && !overrideMorning ? (
                  <button
                    data-testid="coffee-morning-unlock"
                    onClick={() => setOverrideMorning(true)}
                    className="px-3 py-2 rounded-md border border-[#E5E1D8] hover:bg-[#F9F6F0] shrink-0"
                    title="Upravit uloženou hodnotu"
                  >
                    <Pencil className="w-4 h-4" />
                  </button>
                ) : (
                  <button
                    data-testid="coffee-morning-save"
                    disabled={saving || morningLocked}
                    onClick={() => submitPart("morning")}
                    className={`btn-primary shrink-0 ${morningLocked ? "opacity-50 cursor-not-allowed" : ""}`}
                  >
                    Uložit
                  </button>
                )}
              </div>
            </label>

            {/* DOPLNĚNO */}
            <label className="block">
              <div className="text-xs uppercase tracking-wider text-[#57534E] mb-1.5 inline-flex items-center gap-1">
                <Plus className="w-3.5 h-3.5" /> Doplněno během dne (g)
                {eveningSaved && (
                  <span className="ml-1 text-[10px] px-1.5 py-0.5 rounded bg-[#E5E1D8] text-[#57534E]">Uzamčeno</span>
                )}
              </div>
              <div className="flex gap-2">
                <input
                  data-testid="coffee-refill-input"
                  type="number"
                  inputMode="decimal"
                  step="1"
                  min="0"
                  disabled={refillLocked}
                  className={`input-cafe w-full text-xl ${refillLocked ? "opacity-60 cursor-not-allowed" : ""}`}
                  placeholder="0"
                  value={form.refill}
                  onChange={(e) => setForm({ ...form, refill: e.target.value })}
                />
                {isAdmin && eveningSaved && !overrideRefill ? (
                  <button
                    data-testid="coffee-refill-unlock"
                    onClick={() => setOverrideRefill(true)}
                    className="px-3 py-2 rounded-md border border-[#E5E1D8] hover:bg-[#F9F6F0] shrink-0"
                    title="Upravit"
                  >
                    <Pencil className="w-4 h-4" />
                  </button>
                ) : (
                  <button
                    data-testid="coffee-refill-save"
                    disabled={saving || refillLocked}
                    onClick={() => submitPart("refill")}
                    className={`px-3 py-2 rounded-md border border-[#E5E1D8] hover:bg-[#F9F6F0] shrink-0 ${refillLocked ? "opacity-50 cursor-not-allowed" : ""}`}
                  >
                    Uložit
                  </button>
                )}
              </div>
            </label>

            {/* VEČER */}
            <label className="block">
              <div className="text-xs uppercase tracking-wider text-[#57534E] mb-1.5 inline-flex items-center gap-1">
                <Sunset className="w-3.5 h-3.5" /> Večer (g v mlýncích celkem + káva balíčky)
                {eveningSaved && (
                  <span className="ml-1 text-[10px] px-1.5 py-0.5 rounded bg-[#E5E1D8] text-[#57534E]">Uloženo</span>
                )}
              </div>
              <div className="flex gap-2">
                <input
                  data-testid="coffee-evening-input"
                  type="number"
                  inputMode="decimal"
                  step="1"
                  min="0"
                  disabled={eveningLocked}
                  className={`input-cafe w-full text-xl ${eveningLocked ? "opacity-60 cursor-not-allowed" : ""}`}
                  placeholder="např. 220"
                  value={form.evening}
                  onChange={(e) => setForm({ ...form, evening: e.target.value })}
                />
                {isAdmin && eveningSaved && !overrideEvening ? (
                  <button
                    data-testid="coffee-evening-unlock"
                    onClick={() => setOverrideEvening(true)}
                    className="px-3 py-2 rounded-md border border-[#E5E1D8] hover:bg-[#F9F6F0] shrink-0"
                    title="Upravit uloženou hodnotu"
                  >
                    <Pencil className="w-4 h-4" />
                  </button>
                ) : (
                  <button
                    data-testid="coffee-evening-save"
                    disabled={saving || eveningLocked}
                    onClick={() => submitPart("evening")}
                    className={`btn-primary shrink-0 ${eveningLocked ? "opacity-50 cursor-not-allowed" : ""}`}
                  >
                    Uložit
                  </button>
                )}
              </div>
            </label>
          </div>

          {/* POČÍTADLA MLÝNKŮ (večer) */}
          <div className="mt-5 pt-5 border-t border-[#E5E1D8]">
            <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
              <div className="text-xs uppercase tracking-wider text-[#57534E] inline-flex items-center gap-1">
                Počítadla mlýnků — večerní odečet
                {countersSaved && (
                  <span className="ml-1 text-[10px] px-1.5 py-0.5 rounded bg-[#E5E1D8] text-[#57534E]">Uloženo</span>
                )}
              </div>
              {isAdmin && countersSaved && !overrideCounters && (
                <button
                  data-testid="coffee-counters-unlock"
                  onClick={() => setOverrideCounters(true)}
                  className="text-xs px-2 py-1 rounded-md border border-[#E5E1D8] hover:bg-[#F9F6F0] inline-flex items-center gap-1"
                  title="Upravit uložené hodnoty"
                >
                  <Pencil className="w-3.5 h-3.5" /> Upravit
                </button>
              )}
            </div>
            <div className="text-[11px] text-[#A8A29E] mb-3">
              Zapiš hodnotu z displeje počítadla každého mlýnku na konci směny. Rozdíl proti minulému dni ukazuje počet uvařených káv.
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {Array.from({ length: grindersCount }).map((_, idx) => {
                const val = form.counters?.[idx] ?? "";
                return (
                  <label key={idx} className="block">
                    <div className="text-xs text-[#57534E] mb-1">Mlýnek {idx + 1}</div>
                    <input
                      data-testid={`coffee-counter-input-${idx}`}
                      type="number"
                      inputMode="decimal"
                      step="1"
                      min="0"
                      disabled={countersLocked}
                      className={`input-cafe w-full ${countersLocked ? "opacity-60 cursor-not-allowed" : ""}`}
                      placeholder="např. 12345"
                      value={val}
                      onChange={(e) => {
                        const next = [...(form.counters || [])];
                        while (next.length < grindersCount) next.push("");
                        next[idx] = e.target.value;
                        setForm({ ...form, counters: next });
                      }}
                    />
                  </label>
                );
              })}
            </div>
            <div className="mt-3">
              <button
                data-testid="coffee-counters-save"
                disabled={saving || countersLocked}
                onClick={() => submitPart("counters")}
                className={`btn-primary ${countersLocked ? "opacity-50 cursor-not-allowed" : ""}`}
              >
                Uložit odečet počítadel
              </button>
            </div>
          </div>

          {/* PERSONÁLNÍ KÁVA */}
          <div className="mt-5 pt-5 border-t border-[#E5E1D8]">
            <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
              <div className="text-xs uppercase tracking-wider text-[#57534E] inline-flex items-center gap-1">
                Nevyjeté účty s Perso kávou (ks)
                {personalSaved && (
                  <span className="ml-1 text-[10px] px-1.5 py-0.5 rounded bg-[#E5E1D8] text-[#57534E]">Uloženo</span>
                )}
              </div>
            </div>
            <div className="text-[11px] text-[#A8A29E] mb-3">
              Kolik z prodaných káv bylo personálních (staff káva na nevyjetém účtu). Uveď v kusech.
            </div>
            <div className="flex gap-2 max-w-xs">
              <input
                data-testid="coffee-personal-input"
                type="number"
                inputMode="decimal"
                step="1"
                min="0"
                disabled={personalLocked}
                className={`input-cafe w-full text-xl ${personalLocked ? "opacity-60 cursor-not-allowed" : ""}`}
                placeholder="např. 3"
                value={form.personal}
                onChange={(e) => setForm({ ...form, personal: e.target.value })}
              />
              {isAdmin && personalSaved && !overridePersonal ? (
                <button
                  data-testid="coffee-personal-unlock"
                  onClick={() => setOverridePersonal(true)}
                  className="px-3 py-2 rounded-md border border-[#E5E1D8] hover:bg-[#F9F6F0] shrink-0"
                  title="Upravit uloženou hodnotu"
                >
                  <Pencil className="w-4 h-4" />
                </button>
              ) : (
                <button
                  data-testid="coffee-personal-save"
                  disabled={saving || personalLocked}
                  onClick={() => submitPart("personal")}
                  className={`btn-primary shrink-0 ${personalLocked ? "opacity-50 cursor-not-allowed" : ""}`}
                >
                  Uložit
                </button>
              )}
            </div>
          </div>

          {(today?.expected_portions != null || today?.counter_portions != null) && (
            <div
              data-testid="coffee-result"
              className="mt-4 p-4 rounded-lg border border-[#376A4D4D]"
              style={{ background: "#376A4D0F" }}
            >
              <div className="label-eyebrow text-[#376A4D] mb-2">Porovnání prodaných káv</div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <div className="text-xs text-[#57534E] mb-0.5">Podle vážení</div>
                  <div className="text-3xl font-bold mono" style={{ color: "#376A4D", fontFamily: "Outfit" }}>
                    {today?.expected_portions != null ? formatQty(today.expected_portions) : "—"} porcí
                  </div>
                  {today?.expected_portions != null && (
                    <div className="text-[11px] text-[#57534E] mt-1">
                      Spotřeba: {formatQty(today.consumed_grams)} g ÷ {today.portion_grams} g/porce
                    </div>
                  )}
                </div>
                <div>
                  <div className="text-xs text-[#57534E] mb-0.5">Podle počítadel</div>
                  <div className="text-3xl font-bold mono" style={{ color: "#3A2D27", fontFamily: "Outfit" }}>
                    {today?.counter_portions != null ? formatQty(today.counter_portions) : "—"} porcí
                  </div>
                  {today?.counter_portions != null && (
                    <div className="text-[11px] text-[#57534E] mt-1">
                      {formatQty(today.counter_sum)} − {formatQty(today.prev_counter_sum)}
                      {today.counter_source === "initial" && " (start z admin)"}
                    </div>
                  )}
                </div>
                {today?.expected_portions != null && today?.counter_portions != null && (() => {
                  const diff = today.expected_portions - today.counter_portions;
                  const base = today.counter_portions || 1;
                  const pct = (diff / base) * 100;
                  const color = Math.abs(pct) <= 5 ? "#376A4D" : "#9E4747";
                  const sign = diff > 0 ? "+" : "";
                  return (
                    <div data-testid="coffee-diff">
                      <div className="text-xs text-[#57534E] mb-0.5">Rozdíl (vážení − počítadla)</div>
                      <div className="text-3xl font-bold mono" style={{ color, fontFamily: "Outfit" }}>
                        {sign}{formatQty(diff)} porcí
                      </div>
                      <div className="text-[11px] mt-1" style={{ color }}>
                        {sign}{pct.toFixed(1)} %
                      </div>
                    </div>
                  );
                })()}
              </div>
              {today?.counter_portions == null && Array.isArray(today?.counter_readings) && today.counter_readings.length > 0 && (
                <div className="mt-3 text-xs text-[#9E4747]">
                  Nelze porovnat s minulým dnem — chybí předchozí odečet i počáteční stav v administraci pobočky.
                </div>
              )}
              {today?.personal_coffees != null && (
                <div className="mt-3 pt-3 border-t border-[#376A4D33] text-sm" data-testid="coffee-personal-result">
                  <span className="text-[#57534E]">Personální káva (nevyjeté účty):</span>{" "}
                  <strong style={{ color: "#3A2D27" }}>{formatQty(today.personal_coffees)} ks</strong>
                  {today?.counter_portions != null && (
                    <span className="text-xs text-[#57534E] ml-2">
                      · Ostatní prodej: <strong>{formatQty(Math.max(0, today.counter_portions - today.personal_coffees))} ks</strong>
                    </span>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {isAdmin && (
        <div className="card-cafe p-5 sm:p-6">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <div>
              <div className="label-eyebrow">Historie</div>
              <h3 className="text-lg font-semibold" style={{ fontFamily: "Outfit" }}>
                Přehled — {user?.role === "admin" ? "všechny pobočky" : activeBranch?.name || "pobočka"}
              </h3>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <input
                data-testid="coffee-range-from"
                type="date"
                className="input-cafe py-1.5 text-sm"
                value={range.from}
                onChange={(e) => setRange({ ...range, from: e.target.value })}
              />
              <span className="text-xs text-[#57534E]">–</span>
              <input
                data-testid="coffee-range-to"
                type="date"
                className="input-cafe py-1.5 text-sm"
                value={range.to}
                onChange={(e) => setRange({ ...range, to: e.target.value })}
              />
              <span className="text-xs text-[#57534E] ml-2">
                Celkem <strong>{formatQty(totalPortions)}</strong> porcí
              </span>
            </div>
          </div>
          {history.length === 0 ? (
            <div className="text-sm text-[#57534E]">Žádné záznamy v tomto období.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[#F9F6F0]">
                  <tr className="text-left">
                    <th className="p-3 text-xs uppercase tracking-wider text-[#57534E]">Datum</th>
                    <th className="p-3 text-xs uppercase tracking-wider text-[#57534E]">Pobočka</th>
                    <th className="p-3 text-right text-xs uppercase tracking-wider text-[#57534E]">Ráno</th>
                    <th className="p-3 text-right text-xs uppercase tracking-wider text-[#57534E]">Doplněno</th>
                    <th className="p-3 text-right text-xs uppercase tracking-wider text-[#57534E]">Večer</th>
                    <th className="p-3 text-right text-xs uppercase tracking-wider text-[#57534E]">Spotřeba (g)</th>
                    <th className="p-3 text-right text-xs uppercase tracking-wider text-[#57534E]">Porce (vážení)</th>
                    <th className="p-3 text-right text-xs uppercase tracking-wider text-[#57534E]">Počítadla (Σ)</th>
                    <th className="p-3 text-right text-xs uppercase tracking-wider text-[#57534E]">Porce (počítadla)</th>
                    <th className="p-3 text-right text-xs uppercase tracking-wider text-[#57534E]">Perso (ks)</th>
                    <th className="p-3 text-xs uppercase tracking-wider text-[#57534E]">Kdo zadal</th>
                    <th className="p-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {history.map((r) => {
                    const editing = editRow?.id === r.id;
                    return (
                      <tr key={r.id} className="border-t border-[#E5E1D8]" data-testid={`coffee-row-${r.id}`}>
                        <td className="p-3 mono">{r.date}</td>
                        <td className="p-3">{r.branch_name}</td>
                        <td className="p-3 text-right mono">
                          {editing ? (
                            <input
                              type="number"
                              className="input-cafe py-1 text-sm w-24 text-right"
                              value={editRow.morning}
                              onChange={(e) => setEditRow({ ...editRow, morning: e.target.value })}
                              data-testid={`coffee-edit-morning-${r.id}`}
                            />
                          ) : (
                            r.morning_grams != null ? formatQty(r.morning_grams) : "—"
                          )}
                        </td>
                        <td className="p-3 text-right mono">
                          {editing ? (
                            <input
                              type="number"
                              className="input-cafe py-1 text-sm w-20 text-right"
                              value={editRow.refill}
                              onChange={(e) => setEditRow({ ...editRow, refill: e.target.value })}
                              data-testid={`coffee-edit-refill-${r.id}`}
                            />
                          ) : (
                            r.refill_grams ? formatQty(r.refill_grams) : "—"
                          )}
                        </td>
                        <td className="p-3 text-right mono">
                          {editing ? (
                            <input
                              type="number"
                              className="input-cafe py-1 text-sm w-24 text-right"
                              value={editRow.evening}
                              onChange={(e) => setEditRow({ ...editRow, evening: e.target.value })}
                              data-testid={`coffee-edit-evening-${r.id}`}
                            />
                          ) : (
                            r.evening_grams != null ? formatQty(r.evening_grams) : "—"
                          )}
                        </td>
                        <td className="p-3 text-right mono font-semibold">{r.consumed_grams != null ? formatQty(r.consumed_grams) : "—"}</td>
                        <td className="p-3 text-right mono font-bold" style={{ color: r.expected_portions != null ? "#376A4D" : "#A8A29E" }}>
                          {r.expected_portions != null ? formatQty(r.expected_portions) : "—"}
                        </td>
                        <td className="p-3 text-right mono">
                          {Array.isArray(r.counter_readings) && r.counter_readings.length > 0 ? (
                            <span title={r.counter_readings.map((c, i) => `M${i + 1}: ${formatQty(c)}`).join("\n")}>
                              {formatQty(r.counter_sum ?? 0)}
                            </span>
                          ) : "—"}
                        </td>
                        <td className="p-3 text-right mono font-bold" style={{ color: r.counter_portions != null ? "#3A2D27" : "#A8A29E" }}>
                          {r.counter_portions != null ? formatQty(r.counter_portions) : "—"}
                        </td>
                        <td className="p-3 text-right mono">
                          {r.personal_coffees != null ? formatQty(r.personal_coffees) : "—"}
                        </td>
                        <td className="p-3 text-xs text-[#57534E]" data-testid={`coffee-who-${r.id}`}>
                          {(() => {
                            const items = [];
                            if (r.morning_by_name) items.push(<div key="m"><span className="text-[#A8A29E]">Ráno:</span> {r.morning_by_name}</div>);
                            if (r.evening_by_name) items.push(<div key="e"><span className="text-[#A8A29E]">Večer:</span> {r.evening_by_name}</div>);
                            if (r.counter_by_name) items.push(<div key="c"><span className="text-[#A8A29E]">Počítadla:</span> {r.counter_by_name}</div>);
                            if (r.personal_by_name) items.push(<div key="p"><span className="text-[#A8A29E]">Perso:</span> {r.personal_by_name}</div>);
                            if (items.length === 0 && r.created_by_name) items.push(<div key="cr">{r.created_by_name}</div>);
                            return items.length > 0 ? items : "—";
                          })()}
                        </td>
                        <td className="p-3 text-right">
                          {editing ? (
                            <div className="flex items-center justify-end gap-1">
                              <button
                                data-testid={`coffee-edit-save-${r.id}`}
                                onClick={saveEditRow}
                                className="p-1.5 rounded border border-[#376A4D] text-[#376A4D] hover:bg-[#376A4D0F]"
                                title="Uložit"
                              >
                                <Check className="w-3.5 h-3.5" />
                              </button>
                              <button
                                data-testid={`coffee-edit-cancel-${r.id}`}
                                onClick={cancelEditRow}
                                className="p-1.5 rounded border border-[#E5E1D8] hover:bg-[#F9F6F0]"
                                title="Zrušit"
                              >
                                <X className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          ) : (
                            <div className="flex items-center justify-end gap-1">
                              <button
                                data-testid={`coffee-edit-${r.id}`}
                                onClick={() => startEditRow(r)}
                                className="p-1.5 rounded border border-[#E5E1D8] hover:bg-[#F9F6F0]"
                                title="Upravit"
                              >
                                <Pencil className="w-3.5 h-3.5" />
                              </button>
                              <button
                                data-testid={`coffee-delete-${r.id}`}
                                onClick={() => remove(r.id)}
                                className="p-1.5 rounded border border-[#E5E1D8] hover:bg-[#F9F6F0] text-[#9E4747]"
                                title="Smazat"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
